/**
 * @file        src/pages/flight/SearchHistory.jsx
 * @description Flight Search History (read-only) over flight_search_logs.
 * @author      Kinetika Labs
 * @created     2026-07-01
 * @changelog
 *   - 2026-09-22 Claude — Three reported faults on this screen, all of them the
 *                          table telling the truth about a round trip and lying
 *                          about everything else:
 *                          • RETURN DATE. Every one-way showed a Return equal to
 *                            its Departure, and a multicity showed its last hop's
 *                            date. The writer invented both (fixed in
 *                            airSearchController); this screen now also refuses to
 *                            print a return for anything but a `roundtrip`, so a
 *                            tenant DB that has not run the 2026-09-22 migration
 *                            shows "—" instead of the bogus date it still stores.
 *                          • ROUTE. From/To rendered the raw comma-joined leg
 *                            lists ("BOM, DEL" → "DEL, BOM"), which is unreadable
 *                            on a round trip and wrong on a multicity — the To
 *                            column showed the second leg's arrival, not the
 *                            journey's end. Now From is the origin, To is where
 *                            the itinerary actually ends, and a multicity names
 *                            its stops underneath.
 *                          • MULTICITY DATES had nowhere to go — only leg 1's
 *                            date was ever stored, so a three-leg trip showed one
 *                            date and no way to see the rest. The booking side now
 *                            records every leg's date (`leg_dates`); this cell
 *                            still shows ONE date, with "+N more" expanding the
 *                            full itinerary in place, so the table's shape is
 *                            unchanged until someone asks. History has no leg
 *                            dates and says so rather than inventing them.
 *                          • PAX took three lines per row, so a third fewer rows
 *                            fit on screen. One line, non-zero classes only, with
 *                            the full breakdown on hover.
 *   - 2026-08-10 Claude — Added title/subtitle/audience props (defaulting to the
 *                          unchanged B2C behaviour) so CorporateSearchHistory.jsx
 *                          can reuse this page fixed to audience=b2b instead of
 *                          forking it — the backend now actually applies
 *                          `audience` (it previously accepted but ignored it).
 *   - 2026-07-09 Claude — Reference parity: Pax stacks Adult/Child/Infant on
 *                          separate lines (was "1A 0C 0I"). REVERSED 2026-09-22.
 *   - 2026-07-01 Claude — Initial (B2C Flight Management).
 */
import { useState } from 'react';
import { Link } from 'react-router-dom';
import ReadOnlyListPage, { fmtDate } from './ReadOnlyListPage';
import Chip from '../../components/Chip';
import { idCell, customerCell } from './cells';

const day = (v) => (v ? String(v).slice(0, 10) : '—');

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
/** "2026-09-29" → "29 Sep 2026". Used only inside the leg list, where space allows. */
function longDay(v) {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(v || ''));
  return m ? `${Number(m[3])} ${MONTHS[Number(m[2]) - 1]} ${m[1].slice(2)}` : (v ? String(v).slice(0, 10) : '—');
}

/** The stored leg lists are comma-joined text ("BOM, DEL"). Split defensively. */
const codes = (v) =>
  String(v || '')
    .split(',')
    .map((s) => s.trim().toUpperCase())
    .filter(Boolean);

/**
 * Where the itinerary starts and ends.
 *
 * `destination_airport_code` is EVERY leg's arrival, so the journey's end is its
 * LAST entry, not its first — and on a round trip the last entry is the origin
 * again (BOM→DEL, DEL→BOM), which would render "BOM → BOM". A round trip's
 * destination is therefore leg 1's arrival, and only a multicity reads to the end.
 */
function itinerary(row) {
  const from = codes(row.source_airport_code);
  const to = codes(row.destination_airport_code);
  const type = String(row.search_type || '').toLowerCase();
  const origin = from[0] || row.origin_code || null;
  const isMulti = type === 'multicity';
  const dest = isMulti ? to[to.length - 1] : to[0] || row.destination_code || null;
  // The stops a multicity passes through on the way — everything it lands at
  // except its final destination.
  const via = isMulti ? to.slice(0, -1) : [];
  return { origin, dest, via, type };
}

const fromCell = (_v, r) => <span className="mono">{itinerary(r).origin || '—'}</span>;

/**
 * The itinerary leg by leg: leg i flies source[i] → destination[i] on legDates[i].
 * `leg_dates` arrived with the 2026-09-22 migration, so history has none — those
 * legs are listed with their route and an honest "date not recorded" rather than
 * being hidden or given leg 1's date.
 */
function legsOf(row) {
  const from = codes(row.source_airport_code);
  const to = codes(row.destination_airport_code);
  const dates = String(row.leg_dates || '').split(',').map((d) => d.trim()).filter(Boolean);
  return from.map((f, i) => ({ from: f, to: to[i] || null, date: dates[i] || null }));
}

/**
 * Departure date. A multicity has a date PER LEG and only leg 1's was ever shown —
 * the other legs' dates had nowhere to go. The cell still renders one date, so the
 * table's shape is unchanged; "+N more" expands the full itinerary in place, and
 * only for the row that was asked about.
 */
function DepartureCell({ row }) {
  const [open, setOpen] = useState(false);
  const isMulti = String(row.search_type || '').toLowerCase() === 'multicity';
  const legs = legsOf(row);
  if (!isMulti || legs.length < 2) return <span>{day(row.departure_date)}</span>;
  return (
    <div className="sh-dep">
      <span>{day(row.departure_date)}</span>
      <button type="button" className="sh-legs-toggle" aria-expanded={open}
              onClick={() => setOpen((v) => !v)}>
        {open ? 'Hide legs' : `+${legs.length - 1} more`}
      </button>
      {open ? (
        <ol className="sh-legs">
          {legs.map((l, i) => (
            <li key={`${l.from}-${l.to}-${i}`}>
              <span className="mono">{l.from} → {l.to || '—'}</span>
              <span className={l.date ? 'sh-leg-date' : 'sh-leg-date muted'}>
                {l.date ? longDay(l.date) : 'date not recorded'}
              </span>
            </li>
          ))}
        </ol>
      ) : null}
    </div>
  );
}

const toCell = (_v, r) => {
  const { dest, via } = itinerary(r);
  return (
    <div>
      <span className="mono">{dest || '—'}</span>
      {via.length ? (
        <div className="muted" style={{ fontSize: '.78em' }}>via {via.join(' · ')}</div>
      ) : null}
    </div>
  );
};

/**
 * ONLY a round trip has a return date. A one-way's stored `return_date` is its own
 * departure date and a multicity's is its last hop — both written by the old
 * mapper, and both still present on any tenant DB that has not run the 2026-09-22
 * migration. Keying the display on search_type means neither can reach the screen.
 */
const returnCell = (v, r) =>
  String(r.search_type || '').toLowerCase() === 'roundtrip' ? day(v) : <span className="muted">—</span>;

/**
 * Pax on ONE line. Three stacked lines cost a third of the visible rows, and two
 * of them usually read "0". Only the classes actually travelling are shown; the
 * full breakdown stays available on hover for the row that needs it.
 */
const paxCell = (_v, r) => {
  const a = Number(r.num_adults) || 0;
  const c = Number(r.num_children) || 0;
  const i = Number(r.num_infants) || 0;
  const parts = [];
  if (a) parts.push(`${a}A`);
  if (c) parts.push(`${c}C`);
  if (i) parts.push(`${i}I`);
  return (
    <span className="mono" title={`Adult ${a} · Child ${c} · Infant ${i}`}>
      {parts.length ? parts.join(' · ') : '—'}
    </span>
  );
};

// Chip tones are ok|warn|pend|rej (admin.css). One-way is the ordinary case, so
// it keeps the violet `warn` this screen has always used; the two that carry a
// return or extra legs are tinted apart so the Type column can be read at a glance.
const TYPE_TONE = { oneway: 'warn', roundtrip: 'ok', multicity: 'pend' };

export default function SearchHistory({
  title = 'Flight Search History',
  subtitle = 'B2C Flight · customer searches',
  audience = 'b2c',
  // The analytics screen for THIS audience. A prop, not a query string built from
  // `audience` — the audience is fixed by the screen and must not be movable from
  // the URL (CLAUDE.md §9d).
  analyticsTo = '/flight/search-analytics',
} = {}) {
  return (
    <ReadOnlyListPage
      title={title}
      subtitle={subtitle}
      endpoint="/flight/search-history"
      queryRoot={`flight-search-history-${audience}`}
      extraParams={{ audience }}
      searchLabel="Origin / Destination code"
      showIndex={false}
      toolbar={
        <Link className="btn ghost" to={analyticsTo}>View analytics →</Link>
      }
      columns={[
        { key: 'id', label: 'Search ID', render: idCell },
        { key: 'customer_name', label: 'Customer', render: customerCell },
        {
          key: 'search_type',
          label: 'Type',
          render: (v) => (v ? <Chip tone={TYPE_TONE[String(v).toLowerCase()] || 'warn'}>{v}</Chip> : '—'),
        },
        { key: 'source_airport_code', label: 'From', render: fromCell },
        { key: 'destination_airport_code', label: 'To', render: toCell },
        { key: 'departure_date', label: 'Departure', render: (_v, r) => <DepartureCell row={r} /> },
        { key: 'return_date', label: 'Return', render: returnCell },
        { key: 'num_adults', label: 'Pax', render: paxCell },
        { key: 'created_at', label: 'Searched On', render: fmtDate },
      ]}
    />
  );
}
